import * as React from "react";
import { useEffect } from "react";
import { Grid, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import moment, { Moment } from "moment";
import { State } from "../types";
import { connect } from "react-redux";
import { MeetingListFragment, MeetingListFragmentDoc } from "./fragments.generated";
import { GenericTable, useGenericQuery } from "@dvrt/graphql-widgets";
import { useApolloClient } from "@apollo/client";
import { usePageTitle } from "../hooks";
import DeleteMeetingButton from "../components/DeleteMeetingButton";
import { HasPermission } from "../components/tools";
import { Duration } from "conference/components/DurationFields";
import { defineMessage } from "react-intl";

const mapStateToProps = (state: State) => {
    const { lastMeetingId } = state;
    return {
        lastMeetingId,
    };
};

type Props = {
    lastMeetingId: number;
    hideStarted?: boolean;
    hideFuture?: boolean;
};

export default connect(mapStateToProps)(({ lastMeetingId, hideStarted = false, hideFuture = false }: Props) => {
    const parseDate = (value: string): Moment => moment.parseZone(value).local();
    const formatDate = (value: Moment): string => value.format("YYYY-MM-DD HH:mm");
    const client = useApolloClient();

    const genericQuery = useGenericQuery<MeetingListFragment>("meetings", MeetingListFragmentDoc, client, {
        fetchPolicy: "cache-and-network",
        filtering: {
            argumentName: "filter",
            placeHolder: defineMessage({ id: "summary_or_description", defaultMessage: "Summary or description" }),
            idPrefix: "search-meetings",
        },
        extraVariables: {
            hideStarted,
            hideFuture,
        },
    });

    useEffect(() => {
        if (lastMeetingId) genericQuery.refetch();
    }, [lastMeetingId]);

    usePageTitle(defineMessage({ id: "title.meetingList", defaultMessage: "Meeting list" }));

    return (
        <Grid container>
            <Grid
                size={{ lg: "auto", md: 12, xs: 12, sm: 12 }}
                sx={{
                    "& table th": {
                        fontSize: "1.1em",
                        fontWeight: "bold",
                    },
                }}
            >
                <GenericTable<MeetingListFragment>
                    query={genericQuery}
                    labels={{
                        summary: defineMessage({ id: "meetings.summary", defaultMessage: "Summary" }),
                        startTime: defineMessage({ id: "meetings.startTime", defaultMessage: "Start" }),
                        length: defineMessage({ id: "meetings.length", defaultMessage: "Length" }),
                        roomId: defineMessage({ id: "meetings.roomId", defaultMessage: "Room ID" }),
                        pinCode: defineMessage({ id: "meetings.pinCode", defaultMessage: "Pin" }),
                        allowUnmute: defineMessage({ id: "meetings.allowUnmute", defaultMessage: "Allow unmute" }),
                    }}
                    dataRenderers={{
                        summary(value, row) {
                            return (
                                <Link component={RouterLink} to={`/meeting/view/${row.id}`}>
                                    {value}
                                </Link>
                            );
                        },
                        startTime(value) {
                            const momentValue = parseDate(value);
                            return (
                                <span>
                                    {formatDate(momentValue)} (
                                    {moment.duration(momentValue.diff(moment())).humanize(true)})
                                </span>
                            );
                        },
                        length: (value) => <Duration value={value} />,
                    }}
                    extraColumns={
                        hideStarted
                            ? [
                                  {
                                      path: "actions",
                                      label: null,
                                      renderer: (row) => {
                                          return (
                                              <HasPermission name="create_meeting">
                                                  <DeleteMeetingButton
                                                      meetingId={row.id}
                                                      onSuccess={genericQuery.refetch}
                                                      name={row.summary}
                                                  />
                                              </HasPermission>
                                          );
                                      },
                                  },
                              ]
                            : []
                    }
                />
            </Grid>
        </Grid>
    );
});
